import { View } from 'react-native';
import { useState } from 'react';
import { ChatContext } from '@fytness/shared';
import { ChatPromptField } from '~/chat/components/chat-prompt-field/ChatPromptField';
import { ChatWindow } from '~/chat/components/chat-window/ChatWindow';
import { RightSidebar } from '~/layout/components/right-sidebar/RightSidebar';
import { useContextChat } from '~/chat/hooks/useContextChat';
import { useAiCredits } from '~/purchases/hooks/useAiCredits';
import { usePaywall } from '~/purchases/hooks/usePaywall';

export interface SidebarChatProps {
  /** Routing context for the conversation (WORKOUT / MEAL). */
  context: ChatContext;
  /**
   * Active reference within the context (workout id, or "yyyy-MM-dd" meal day).
   * `null` while the host screen is still resolving it.
   */
  contextRefId: string | null;
  /** Header title shown above the conversation. */
  title: string;
  /** Context-specific input placeholder (workout vs meal). */
  placeholder: string;
}

/**
 * Right-rail chat panel scoped to a single workout / meal day. Reuses the same
 * message list ({@link ChatWindow}) and input ({@link ChatPromptField}) as the
 * full chat screen, with the shared rail header and a compact single-line input.
 * The caller is responsible for the feature-flag gate (the rail omits the tab
 * when chat is disabled).
 */
export const SidebarChat = ({
  context,
  contextRefId,
  title,
  placeholder,
}: SidebarChatProps) => {
  const [input, setInput] = useState('');
  const { messages, sendMessage, loading, thinking } = useContextChat({
    context,
    contextRefId,
  });
  const { credits } = useAiCredits();
  const { openPaywall } = usePaywall();

  const onSubmit = () => {
    if (!input.trim() || loading || thinking) {
      return;
    }
    // A drained FYT wallet blocks sending — open the paywall instead. `null`
    // means still loading/unauthed, so the backend stays the authority.
    if (credits && credits.balance <= 0) {
      openPaywall();
      return;
    }
    sendMessage(input);
    setInput('');
  };

  return (
    <View className="flex-1">
      <RightSidebar.PanelHeader icon="Bot" title={title} />

      <ChatWindow messages={messages} thinking={thinking} compact />

      <ChatPromptField
        value={input}
        onChangeText={setInput}
        onSubmit={onSubmit}
        placeholder={placeholder}
        disabled={loading || thinking}
        compact
      />
    </View>
  );
};
